import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import FWOSelectInput from '../../form/FWOSelectInput'
import { buildLookupSelectItems } from '../../../utilities/commonFunctions'
import {
  IFeedbackLookupData,
  ILookupItem,
  IServiceArea,
  IServicePoint,
  FeedbackFieldMapping,
} from '../../../models/Feedback'
import Conditional from '../../common/Conditional'
import { useField } from 'formik'
import FWOLimitedTextAreaInput from '../../form/FWOLimitedTextAreaInput'
import DOMConditional from '../../common/DOMConditional'
import FWOTextInput from '../../form/FWOTextInput'

export interface IComplaintProps {
  serviceAreaId: string
  servicePointId: string
  serviceIssueId: string
  issue: string
  libraryPageReference: string
  lookupData: IFeedbackLookupData
}

const Complaint = forwardRef((props: IComplaintProps, ref) => {
  const mountedRef = useRef(false)
  const [, , serviceAreaHelpers] = useField(props.serviceAreaId)
  const [, , servicePoinHelpers] = useField(props.servicePointId)
  const [, , serviceIssueHelpers] = useField(props.serviceIssueId)
  const [, , issueHelpers] = useField(props.issue)
  const [lookupData, setLookupData] = useState<IFeedbackLookupData>()
  const [showLibraryPageReferenceField, setShowLibraryPageReferenceField] =
    useState(false)
  const [options, setOptions] = useState({
    serviceAreaSelectItems: [],
    servicePointSelectItems: [],
    serviceIssueSelectItems: [],
    selectedServiceAreaId: '',
    selectedServicePointId: '',
  })
  const defaultMoreDetailsLabel = `${FeedbackFieldMapping.get(
    'issue'
  )} (1000 character limit)`
  const moreDetailsInfoLabel = `${defaultMoreDetailsLabel}, if related to a specific web page, please provide a URL link`
  const [moreDetailsLabel, setMoreDetailsLabel] = useState<string>(
    defaultMoreDetailsLabel
  )

  useImperativeHandle(ref, () => ({
    clear() {
      serviceAreaHelpers.setValue('')
      servicePoinHelpers.setValue('')
      serviceIssueHelpers.setValue('')
      issueHelpers.setValue('')
    },
  }))

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      setLookupData(props.lookupData)
      const serviceAreas: ILookupItem[] = props.lookupData?.serviceAreas.map(
        (x) => {
          return { id: x.id, name: x.name }
        }
      )

      setOptions((prevState) => {
        return {
          ...prevState,
          serviceAreaSelectItems: buildLookupSelectItems(serviceAreas, true),
          servicePointSelectItems: [],
          serviceIssueSelectItems: [],
        }
      })
    }
    return () => {
      mountedRef.current = false
    }
  }, [])

  const serviceAreaSelected = (value: string) => {
    const serviceArea: IServiceArea = lookupData.serviceAreas?.find(
      (p) => p.id === value
    )
    if (serviceArea) {
      serviceArea.name.includes('website')
        ? setMoreDetailsLabel(moreDetailsInfoLabel)
        : setMoreDetailsLabel(defaultMoreDetailsLabel)

      const servicePoints: ILookupItem[] = serviceArea.servicePoints.map(
        (x) => {
          return { id: x.id, name: x.name }
        }
      )

      if (servicePoints?.length === 1) {
        const servicePointId = servicePoints[0].id
        servicePoinHelpers.setValue(servicePointId)
        const servicePoint: IServicePoint = serviceArea.servicePoints?.find(
          (p) => p.id === servicePointId
        )

        setOptions((prevState) => {
          return {
            ...prevState,
            selectedServiceAreaId: value,
            servicePointSelectItems: [],
            serviceIssueSelectItems: buildLookupSelectItems(
              servicePoint.serviceIssues,
              true
            ),
          }
        })

        return
      }

      setOptions((prevState) => {
        return {
          ...prevState,
          selectedServiceAreaId: value,
          servicePointSelectItems: buildLookupSelectItems(servicePoints, true),
          serviceIssueSelectItems: [],
        }
      })
    } else {
      setOptions((prevState) => {
        return {
          ...prevState,
          selectedServiceAreaId: '',
          servicePointSelectItems: [],
          serviceIssueSelectItems: [],
        }
      })
    }
  }

  const servicePointSelected = (value: string) => {
    const serviceArea: IServiceArea = lookupData.serviceAreas?.find(
      (p) => p.id === options.selectedServiceAreaId
    )
    if (serviceArea) {
      const servicePoint: IServicePoint = serviceArea.servicePoints?.find(
        (p) => p.id === value
      )
      if (servicePoint) {
        setOptions((prevState) => {
          return {
            ...prevState,
            selectedServicePointId: value,
            serviceIssueSelectItems: buildLookupSelectItems(
              servicePoint.serviceIssues,
              true
            ),
          }
        })
      }
    } else {
      setOptions((prevState) => {
        return {
          ...prevState,
          selectedServiceAreaId: '',
          selectedServicePointId: '',
          servicePointSelectItems: [],
          serviceIssueSelectItems: [],
        }
      })
    }
  }
  const onIssueSelection = (value: string) => {
    const serviceArea: IServiceArea = lookupData.serviceAreas?.find(
      (p) => p.id === options.selectedServiceAreaId
    )

    if (serviceArea) {
      const servicePoints = serviceArea.servicePoints?.find(
        (p) => p.id === options.selectedServicePointId
      )

      if (servicePoints) {
        const issueSelected = servicePoints.serviceIssues?.find(
          (p) => p.id === value
        )?.name
        if (issueSelected != null) {
          setShowLibraryPageReferenceField(
            issueSelected === 'Information in a library article'
          )
        }
      }
    }
  }
  return (
    <div>
      <fieldset id="complaintSection" className="complaint-fieldset-title">
        <legend> About </legend>
        <FWOSelectInput
          className="serviceArea"
          name={props.serviceAreaId}
          label=""
          placeholder={'Choose one'}
          required={true}
          options={options.serviceAreaSelectItems}
          loading={false}
          onSelection={serviceAreaSelected}
        />
        <DOMConditional show={options.servicePointSelectItems?.length > 1}>
          <Conditional if={options.servicePointSelectItems?.length > 1}>
            <FWOSelectInput
              className="servicePoint"
              name={props.servicePointId}
              label="My feedback is about "
              placeholder={'Choose one'}
              required={true}
              options={options.servicePointSelectItems}
              loading={false}
              onSelection={servicePointSelected}
            />
          </Conditional>
        </DOMConditional>
        <Conditional if={options.serviceIssueSelectItems?.length > 1}>
          <FWOSelectInput
            className="serviceIssue"
            name={props.serviceIssueId}
            label="Issue "
            placeholder={'Choose one'}
            required={true}
            options={options.serviceIssueSelectItems}
            loading={false}
            onSelection={onIssueSelection}
          />
        </Conditional>

        <Conditional if={showLibraryPageReferenceField}>
          <FWOTextInput
            type="text"
            name={props.libraryPageReference}
            label="Library page reference or article title (if known - optional)"
            ariaLabel="Library page reference or article title"
            placeholder="Please provide library page reference or article title"
            required={false}
          ></FWOTextInput>
        </Conditional>
      </fieldset>
      <fieldset>
        <FWOLimitedTextAreaInput
          name={props.issue}
          label={moreDetailsLabel}
          placeholder="Please provide more detail"
          required={true}
          aria-label="Please provide more detail"
          rows={5}
          limit={1000}
        />
      </fieldset>
    </div>
  )
})

Complaint.displayName = 'Complaint'
export default Complaint
